import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HistoryIcon from '@mui/icons-material/History'
import RemoveIcon from '@mui/icons-material/Remove'
import {
    askBhanzuBot,
    loadBotHistory,
    minimiseBot,
    newBotConversation,
    openBot,
    openBotConversation,
    restoreBot,
    retryBhanzuBot,
    setBotDraft,
    showBotChat,
    showBotHistory,
} from '../../features/assistantSlice'
import {
    pageTitleFor,
    readBotStorage,
    writeBotStorage,
} from '../../helpers/assistant'
import { BOT, RADIUS, TEXT } from '../../theme/tokens'
import ChatView from './ChatView'
import Composer from './Composer'
import HistoryView from './HistoryView'
import Launcher, { BOT_Z_INDEX } from './Launcher'
import { PANEL_EDGE, PANEL_WIDTH } from './layout'

/** A header button: an icon with its name as tooltip and accessible name. */
const HeaderButton = ({ label, onClick, children }) => (
    <Tooltip title={label}>
        <IconButton
            size="small"
            aria-label={label}
            onClick={onClick}
            sx={{ color: TEXT.secondary }}
        >
            {children}
        </IconButton>
    </Tooltip>
)

HeaderButton.propTypes = {
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    children: PropTypes.node,
}

/**
 * BhanzuBot, the assistant in the corner of every page.
 *
 * Mounted once in Layout for a signed-in user, with its state in the store
 * (features/assistantSlice), so the panel and the conversation survive
 * every route change. The open conversation and whether the panel is open
 * are remembered in this browser; a remembered conversation that is gone
 * (404) is forgotten. Each question carries the page it is asked from.
 */
const BhanzuBot = () => {
    const dispatch = useDispatch()
    const { pathname } = useLocation()
    const bot = useSelector((state) => state.assistant)
    const restored = useRef(false)

    // What this browser remembered: the panel, and the conversation to load.
    useEffect(() => {
        const remembered = readBotStorage()
        dispatch(restoreBot(remembered))
        if (remembered.conversationId)
            dispatch(openBotConversation(remembered.conversationId))
    }, [])

    // Remember it again whenever it changes (not on the first render, which
    // still holds the defaults the restore above is replacing).
    useEffect(() => {
        if (!restored.current) {
            restored.current = true
            return
        }
        writeBotStorage({ conversationId: bot.conversationId, open: bot.open })
    }, [bot.conversationId, bot.open])

    const page = { path: pathname, title: pageTitleFor(pathname) }

    const ask = (text) => {
        const message = text.trim()
        if (!message || bot.pending) return
        dispatch(
            askBhanzuBot({
                conversationId: bot.conversationId,
                message,
                page,
            })
        )
    }

    const retry = (questionId) =>
        dispatch(
            retryBhanzuBot({
                conversationId: bot.conversationId,
                questionId,
                page,
            })
        )

    if (!bot.open) return <Launcher onOpen={() => dispatch(openBot())} />

    const inHistory = bot.view === 'history'
    return (
        <Box
            component="section"
            aria-label="BhanzuBot"
            sx={{
                position: 'fixed',
                zIndex: BOT_Z_INDEX,
                right: { xs: 8, sm: PANEL_EDGE },
                left: { xs: 8, sm: 'auto' },
                bottom: { xs: 8, sm: PANEL_EDGE },
                width: { xs: 'auto', sm: PANEL_WIDTH },
                height: { xs: '75vh', sm: 560 },
                maxHeight: { xs: '75vh', sm: '70vh' },
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: BOT.panelBg,
                border: `1px solid ${BOT.panelBorder}`,
                borderRadius: `${RADIUS.md}px`,
                boxShadow: BOT.panelShadow,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 1,
                    borderBottom: `1px solid ${BOT.panelBorder}`,
                }}
            >
                {inHistory && (
                    <HeaderButton
                        label="Back to the conversation"
                        onClick={() => dispatch(showBotChat())}
                    >
                        <ArrowBackIcon fontSize="small" />
                    </HeaderButton>
                )}
                <Typography
                    component="h2"
                    sx={{
                        typography: 'h6',
                        color: TEXT.primary,
                    }}
                >
                    {inHistory ? 'Conversations' : 'BhanzuBot'}
                </Typography>
                {!inHistory && (
                    <Chip
                        label="AI"
                        size="small"
                        sx={{
                            height: 18,
                            typography: 'caption',
                            fontWeight: 700,
                            backgroundColor: BOT.chipBg,
                            color: BOT.chipText,
                        }}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <HeaderButton
                    label="New conversation"
                    onClick={() => dispatch(newBotConversation())}
                >
                    <AddCommentOutlinedIcon fontSize="small" />
                </HeaderButton>
                {!inHistory && (
                    <HeaderButton
                        label="History"
                        onClick={() => dispatch(showBotHistory())}
                    >
                        <HistoryIcon fontSize="small" />
                    </HeaderButton>
                )}
                <HeaderButton
                    label="Minimise"
                    onClick={() => dispatch(minimiseBot())}
                >
                    <RemoveIcon fontSize="small" />
                </HeaderButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {inHistory ? (
                    <HistoryView
                        history={bot.history}
                        currentId={bot.conversationId}
                        onLoad={(cursor) => dispatch(loadBotHistory(cursor))}
                        onOpen={(id) => dispatch(openBotConversation(id))}
                    />
                ) : (
                    <ChatView
                        pathname={pathname}
                        messages={bot.messages}
                        pendingQuestion={bot.pendingQuestion}
                        pending={bot.pending}
                        loadingConversation={bot.loadingConversation}
                        error={bot.error}
                        disabled={bot.disabled}
                        onAsk={ask}
                        onRetry={retry}
                    />
                )}
            </Box>

            {!inHistory && (
                <Composer
                    value={bot.draft}
                    onChange={(text) => dispatch(setBotDraft(text))}
                    onSend={() => ask(bot.draft)}
                    busy={bot.pending || bot.loadingConversation}
                    disabled={bot.disabled}
                    focusToken={bot.focusToken}
                />
            )}
        </Box>
    )
}

export default BhanzuBot
